/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/components/layout/DocumentBranding.tsx
 *
 * Rôle (projet « branding runtime multi-salon », 2026-07-06) :
 *   Pilote À CHAUD les éléments du <head> qui étaient FIGÉS au build dans
 *   `index.html`, donc impossibles à personnaliser sans rebuild :
 *     - `document.title`                       ← nom de l'événement ;
 *     - `<meta name="apple-mobile-web-app-title">` ← nom de l'événement ;
 *     - `<meta name="theme-color">`            ← couleur primaire du branding.
 *
 *   Source = l'événement actif (`useEventConfig` → `tne_events`, éditable au
 *   back-office). Un clone (nouveau salon) change son nom + sa couleur en
 *   base et ces éléments suivent SANS rebuild.
 *
 *   Zéro-régression : si l'événement n'a pas de couleur primaire dans son
 *   branding, on NE touche PAS `theme-color` (le hex figé d'index.html reste).
 *   Le titre suit toujours le nom de l'événement (défaut = « NOLAS 2026 »).
 *
 *   Composant d'effet (ne rend aucun DOM). Monté UNE fois au root de l'app
 *   (App.tsx), donc actif sur TOUTES les routes (y compris splash / login
 *   qui sont hors AppLayout).
 *
 * Voir : hooks/useEventConfig.ts, components/layout/EventThemeApplier.tsx
 *        (palette CSS), index.html (valeurs figées de repli),
 *        app/public_html/manifest.php (manifeste PWA dynamique).
 *
 * @author Mika
 * @since  2026-07-06
 */

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useEventConfig } from '@/hooks/useEventConfig';
import { cleDeTitrePour } from '@/utils/titresDeRoute';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Crée/met à jour une balise `<meta name>` du document. */
function setMetaContent(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function DocumentBranding(): null {
  const { event } = useEventConfig();
  const intl = useIntl();
  const { pathname } = useLocation();
  const name = event.name;
  const primary = event.branding?.palette?.primary;

  /**
   * Chantier J (accessibilité) : WCAG 2.4.2, RGAA 8.5.
   *
   * Ce composant posait `document.title = event.name`, c'est-à-dire le MÊME
   * titre sur les ~140 écrans. Le titre du document est pourtant la toute
   * première chose qu'annonce un lecteur d'écran après une navigation : « le
   * même partout » revient à ne rien dire de l'endroit où l'on vient
   * d'arriver, et rend l'historique et les onglets tout aussi indistincts.
   *
   * On compose désormais « <écran> (<événement>) ». Le nom de l'écran vient
   * d'une table route → clé i18n (utils/titresDeRoute.ts), donc AUCUN écran
   * n'a de code à écrire pour être titré, et le nom de l'événement continue
   * de venir de la configuration : il n'est écrit en dur nulle part.
   *
   * `apple-mobile-web-app-title` garde le nom SEUL : c'est le nom de l'icône
   * sur l'écran d'accueil, pas le titre d'une page.
   */
  const titreEcran = intl.formatMessage({ id: cleDeTitrePour(pathname) });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (name && name.trim() !== '') {
      document.title = intl.formatMessage(
        { id: 'acces.titre.gabarit' },
        { ecran: titreEcran, evenement: name },
      );
      setMetaContent('apple-mobile-web-app-title', name);
    } else {
      // Config d'événement pas encore chargée : le titre de l'écran vaut
      // toujours mieux qu'un titre générique figé dans index.html.
      document.title = titreEcran;
    }
    if (typeof primary === 'string' && HEX_RE.test(primary)) {
      setMetaContent('theme-color', primary);
    }
  }, [name, primary, titreEcran, intl]);

  return null;
}

export default DocumentBranding;
